import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { VehicleReminder, ReminderType } from '../../types/entities';
import { reminderAPI, CreateReminderRequest, ResolveReminderRequest } from '../../api/reminders';
import toast from 'react-hot-toast';
import { Plus, FileText, Wrench, Check, Trash2, X, Calendar, Gauge } from 'lucide-react';

interface Props {
  vehicleId: number;
  currentOdometer?: number;
}

interface ReminderFormData {
  type: ReminderType;
  name: string;
  expiryDate?: string;
  dueOdometer?: number;
  note?: string;
}

interface ResolveFormData {
  nextExpiryDate?: string;
  nextDueOdometer?: number;
}

export const VehicleReminders: React.FC<Props> = ({ vehicleId, currentOdometer = 0 }) => {
  const { t } = useTranslation();
  const [reminders, setReminders] = useState<VehicleReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [resolveReminder, setResolveReminder] = useState<VehicleReminder | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ReminderFormData>({
    defaultValues: {
      type: 'DOCUMENT',
    },
  });

  const {
    register: registerResolve,
    handleSubmit: handleSubmitResolve,
    reset: resetResolve,
    setValue: setResolveValue,
  } = useForm<ResolveFormData>();

  const watchType = watch('type');

  useEffect(() => {
    loadReminders();
  }, [vehicleId]);

  const loadReminders = async () => {
    try {
      setLoading(true);
      const data = await reminderAPI.getByVehicle(vehicleId, true);
      setReminders(data);
    } catch (error) {
      toast.error(t('reminders.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const onSubmitAdd = async (data: ReminderFormData) => {
    setSubmitting(true);
    try {
      const request: CreateReminderRequest = {
        vehicleId,
        type: data.type,
        name: data.name,
        note: data.note,
      };

      if (data.type === 'DOCUMENT' && data.expiryDate) {
        request.expiryDate = data.expiryDate;
      } else if (data.type === 'MAINTENANCE' && data.dueOdometer) {
        request.dueOdometer = data.dueOdometer;
      }

      await reminderAPI.create(request);
      toast.success(t('reminders.createSuccess'));
      reset({ type: 'DOCUMENT' });
      setShowAddForm(false);
      loadReminders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitResolve = async (data: ResolveFormData) => {
    if (!resolveReminder) return;

    setSubmitting(true);
    try {
      const request: ResolveReminderRequest = {};

      if (resolveReminder.type === 'DOCUMENT' && data.nextExpiryDate) {
        request.nextExpiryDate = data.nextExpiryDate;
      } else if (resolveReminder.type === 'MAINTENANCE' && data.nextDueOdometer) {
        request.nextDueOdometer = data.nextDueOdometer;
      }

      await reminderAPI.resolve(resolveReminder.id, request);
      toast.success(t('reminders.resolveSuccess'));
      setResolveReminder(null);
      resetResolve();
      loadReminders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('reminders.deleteConfirm'))) return;

    try {
      await reminderAPI.delete(id);
      toast.success(t('reminders.deleteSuccess'));
      loadReminders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    }
  };

  const openResolveModal = (reminder: VehicleReminder) => {
    setResolveReminder(reminder);
    if (reminder.type === 'MAINTENANCE') {
      // Default to current odometer + 5000km
      setResolveValue('nextDueOdometer', currentOdometer + 5000);
    } else {
      // Default to 1 year from now
      const nextYear = new Date();
      nextYear.setFullYear(nextYear.getFullYear() + 1);
      setResolveValue('nextExpiryDate', nextYear.toISOString().split('T')[0]);
    }
  };

  const getReminderStatus = (reminder: VehicleReminder) => {
    if (reminder.isResolved) {
      return { text: t('reminders.resolved'), color: 'text-green-600 bg-green-50' };
    }

    if (reminder.type === 'DOCUMENT' && reminder.expiryDate) {
      const daysLeft = Math.ceil(
        (new Date(reminder.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
      if (daysLeft <= 0) {
        return { text: t('reminders.overdue'), color: 'text-red-600 bg-red-50' };
      }
      return { text: t('reminders.daysLeft', { days: daysLeft }), color: 'text-yellow-600 bg-yellow-50' };
    }

    if (reminder.type === 'MAINTENANCE' && reminder.dueOdometer) {
      const kmLeft = reminder.dueOdometer - currentOdometer;
      if (kmLeft <= 0) {
        return { text: t('reminders.overdue'), color: 'text-red-600 bg-red-50' };
      }
      return { text: t('reminders.kmLeft', { km: kmLeft.toLocaleString('vi-VN') }), color: 'text-yellow-600 bg-yellow-50' };
    }

    return { text: '', color: '' };
  };

  const pendingReminders = reminders.filter((r) => !r.isResolved);
  const resolvedReminders = reminders.filter((r) => r.isResolved);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t('reminders.title')}</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>{t('reminders.addReminder')}</span>
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="card border-2 border-primary-200">
          <form onSubmit={handleSubmit(onSubmitAdd)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('reminders.type')}</label>
                <select {...register('type')} className="input-field">
                  <option value="DOCUMENT">{t('reminders.document')}</option>
                  <option value="MAINTENANCE">{t('reminders.maintenance')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('reminders.name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('name', { required: t('validation.required') })}
                  className="input-field"
                  placeholder={watchType === 'DOCUMENT' ? 'Đăng kiểm, Bảo hiểm...' : 'Thay nhớt, Bảo dưỡng...'}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>
            </div>

            {watchType === 'DOCUMENT' && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('reminders.expiryDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  {...register('expiryDate', { required: t('validation.required') })}
                  className="input-field"
                />
                {errors.expiryDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.expiryDate.message}</p>
                )}
              </div>
            )}

            {watchType === 'MAINTENANCE' && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('reminders.dueOdometer')} (km) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  {...register('dueOdometer', {
                    required: t('validation.required'),
                    valueAsNumber: true,
                    min: { value: 0, message: t('validation.positiveNumber') },
                  })}
                  className="input-field"
                  placeholder={`Hiện tại: ${currentOdometer.toLocaleString('vi-VN')} km`}
                />
                {errors.dueOdometer && (
                  <p className="text-red-500 text-sm mt-1">{errors.dueOdometer.message}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">{t('common.note')}</label>
              <textarea
                {...register('note')}
                className="input-field"
                rows={2}
                placeholder={t('reminders.notePlaceholder')}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  reset({ type: 'DOCUMENT' });
                }}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button type="submit" disabled={submitting} className="btn-primary">
                {submitting ? t('common.loading') : t('common.add')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pending Reminders */}
      {pendingReminders.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-500">{t('reminders.pending')}</h4>
          {pendingReminders.map((reminder) => {
            const status = getReminderStatus(reminder);
            return (
              <div
                key={reminder.id}
                className="card flex items-center justify-between hover:shadow-md transition-shadow"
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-lg ${reminder.type === 'DOCUMENT' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                    {reminder.type === 'DOCUMENT' ? (
                      <FileText className="w-5 h-5" />
                    ) : (
                      <Wrench className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{reminder.name}</p>
                    <div className="flex items-center space-x-3 text-sm text-gray-500">
                      {reminder.type === 'DOCUMENT' && reminder.expiryDate && (
                        <span className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(reminder.expiryDate).toLocaleDateString('vi-VN')}</span>
                        </span>
                      )}
                      {reminder.type === 'MAINTENANCE' && reminder.dueOdometer && (
                        <span className="flex items-center space-x-1">
                          <Gauge className="w-4 h-4" />
                          <span>{reminder.dueOdometer.toLocaleString('vi-VN')} km</span>
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs ${status.color}`}>
                        {status.text}
                      </span>
                    </div>
                    {reminder.note && (
                      <p className="text-sm text-gray-400 mt-1">{reminder.note}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => openResolveModal(reminder)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title={t('reminders.resolve')}
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(reminder.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Resolved Reminders */}
      {resolvedReminders.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-500">{t('reminders.resolvedList')}</h4>
          {resolvedReminders.map((reminder) => (
            <div
              key={reminder.id}
              className="card flex items-center justify-between opacity-60"
            >
              <div className="flex items-center space-x-4">
                <div className="p-2 rounded-lg bg-green-100 text-green-600">
                  <Check className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium line-through">{reminder.name}</p>
                  <div className="flex items-center space-x-3 text-sm text-gray-500">
                    {reminder.type === 'DOCUMENT' && reminder.expiryDate && (
                      <span>{new Date(reminder.expiryDate).toLocaleDateString('vi-VN')}</span>
                    )}
                    {reminder.type === 'MAINTENANCE' && reminder.dueOdometer && (
                      <span>{reminder.dueOdometer.toLocaleString('vi-VN')} km</span>
                    )}
                    <span className="px-2 py-0.5 rounded-full text-xs text-green-600 bg-green-50">
                      {t('reminders.resolved')}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleDelete(reminder.id)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title={t('common.delete')}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {reminders.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t('reminders.noReminders')}</p>
        </div>
      )}

      {/* Resolve Modal */}
      {resolveReminder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('reminders.resolveTitle')}</h3>
              <button
                onClick={() => {
                  setResolveReminder(null);
                  resetResolve();
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitResolve(onSubmitResolve)} className="p-4 space-y-4">
              <p className="text-gray-600">
                {t('reminders.resolveDescription', { name: resolveReminder.name })}
              </p>

              {resolveReminder.type === 'DOCUMENT' && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('reminders.nextExpiryDate')}
                  </label>
                  <input
                    type="date"
                    {...registerResolve('nextExpiryDate')}
                    className="input-field"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('reminders.nextExpiryHint')}
                  </p>
                </div>
              )}

              {resolveReminder.type === 'MAINTENANCE' && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('reminders.nextOdometer')} (km)
                  </label>
                  <input
                    type="number"
                    {...registerResolve('nextDueOdometer', { valueAsNumber: true })}
                    className="input-field"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('reminders.nextOdometerHint', { current: currentOdometer.toLocaleString('vi-VN') })}
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setResolveReminder(null);
                    resetResolve();
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" disabled={submitting} className="btn-primary">
                  {submitting ? t('common.loading') : t('common.confirm')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
